<script setup lang="ts">
import { defineAsyncComponent, onMounted } from 'vue'

/* Types */
import { DeliveryMethods } from '@/api/modules/basket/types'
import { BasketPayment } from '@/types/api/api-v3/paymentMethods'

/* Stores */
import { useBasketStore } from '~/store/basket/basket'
import { useBasketPointStore } from '~/store/basket/basket-point'
import { useAddressesStore } from '~/store/addresses/addresses'
import { useBasketPaymentMethodsStore } from '~/store/basket/basket-payment-methods'

/* Components */
import BasketTitle from '~/components/Basket/basket-utils/BasketTitle.vue'
import BasketCourier from '~/components/Basket/basket-addresses/BasketCourier.vue'

const BasketPickup = defineAsyncComponent(
  () => import('~/components/Basket/basket-addresses/BasketPickup.vue'),
)
const BasketHall = defineAsyncComponent(
  () => import('~/components/Basket/basket-addresses/BasketHall.vue'),
)

const basketStore = useBasketStore()
const pointStore = useBasketPointStore()
const addressesStore = useAddressesStore()
const basketPaymentMethodsStore = useBasketPaymentMethodsStore()

const { $sentryCaptureException } = useNuxtApp()

const emit = defineEmits(['deliveryMethodChanged'])

const deliveryMethod = computed(() => basketStore.data.delivery.method)

const setModelValueDeliveryMethod = (value: DeliveryMethods) => {
  basketStore.setDeliveryMethodInState(value)
}

const pickupPoints = computed(() => pointStore.getFilteredPointsByTag('pickup'))

const hallPoints = computed(() => pointStore.getFilteredPointsByTag('hall'))

const courierPoints = computed(() => {
  return addressesStore.addresses
})

const showPickupTab = computed(() => pickupPoints.value.data.length !== 0)

const showHallTab = computed(() => hallPoints.value.data.length !== 0)

onMounted(async () => {
  await Promise.all([pointStore.getPoints(), addressesStore.getAddresses()])

  if (pickupPoints.value.error || courierPoints.value.error)
    $sentryCaptureException(
      pickupPoints.value.error || courierPoints.value.error,
    )

  window.carrotquest?.track('Выбрана доставка', {})
})

const selectIsPaymentMethod = (paymentMethod: BasketPayment | null) =>
  basketStore.setSelectedPaymentMethod(paymentMethod)

const deliveryMethodChanged = async () => {
  emit('deliveryMethodChanged')

  // Обновляем список оплат при смене адреса
  await basketPaymentMethodsStore.getPaymentMethods()

  // Устанавливаем первый из списка оплат
  if (basketPaymentMethodsStore.data.length)
    selectIsPaymentMethod(basketPaymentMethodsStore.data[0])
}

const selectDeliveryCourier = () => {
  setDeliveryMethod(DeliveryMethods.PICKUP)
}

const setDeliveryMethod = async (newDeliveryMethod: DeliveryMethods) => {
  let points = pickupPoints as
    | typeof pickupPoints
    | typeof hallPoints
    | typeof courierPoints

  switch (newDeliveryMethod) {
    case DeliveryMethods.HALL:
      points = hallPoints
      break
    case DeliveryMethods.COURIER:
      points = courierPoints
      break
  }

  // Для доставки формируем данные для бэкэнда
  if (newDeliveryMethod === DeliveryMethods.COURIER) {
    // Для доставки курьером проверяем существует ли адрес в зоне доставки
    if (!points.value.data[0] || !points.value.data[0].is_in_delivery_area) {
      return
    }

    const dataSetDeliveryAddress = {
      delivery_method: newDeliveryMethod,
      // @ts-ignore
      address_id: !points.value.data[0].is_in_delivery_area
        ? null
        : points.value.data[0]?.id,
    }

    await basketStore.setDeliveryMethod({
      ...dataSetDeliveryAddress,
    })
  } else {
    const dataSetHallOrPickup = {
      delivery_method: newDeliveryMethod,
      point_id: points.value.data[0]?.id as number | undefined,
    }

    await basketStore.setDeliveryMethod({
      ...dataSetHallOrPickup,
    })
  }

  await deliveryMethodChanged()

  const carrotquestTracks = {
    courier: 'Выбрана доставка',
    pickup: 'Выбран самовывоз',
    hall: 'Выбран заказ в зал',
  }

  window.carrotquest?.track(carrotquestTracks[newDeliveryMethod], {})
}
</script>

<template>
  <section id="addresses" class="addresses">
    <basket-title title="Адрес" />

    <v-tabs
      :model-value="deliveryMethod"
      :class="{ 'tabs--one-tab': !showPickupTab && !showHallTab }"
      class="tabs"
      show-arrows
      @update:model-value="
        setModelValueDeliveryMethod($event as DeliveryMethods)
      "
    >
      <v-tab
        value="courier"
        :ripple="false"
        @click="setDeliveryMethod(DeliveryMethods.COURIER)"
      >
        Доставка
      </v-tab>
      <v-tab
        v-if="showPickupTab"
        value="pickup"
        :ripple="false"
        @click="setDeliveryMethod(DeliveryMethods.PICKUP)"
      >
        Самовывоз
      </v-tab>
      <v-tab
        v-if="showHallTab"
        value="hall"
        :ripple="false"
        @click="setDeliveryMethod(DeliveryMethods.HALL)"
      >
        В ресторане
      </v-tab>
    </v-tabs>

    <v-window v-model="deliveryMethod" class="items">
      <v-window-item
        value="courier"
        transition="tab-fade-transition"
        reverse-transition="tab-fade-transition"
      >
        <basket-courier
          v-if="deliveryMethod === 'courier'"
          @delivery-method-changed="deliveryMethodChanged"
          @select-delivery-courier="selectDeliveryCourier"
        />
      </v-window-item>
      <v-window-item
        v-show="showPickupTab"
        value="pickup"
        transition="tab-fade-transition"
        reverse-transition="tab-fade-transition"
      >
        <basket-pickup
          v-if="deliveryMethod === 'pickup'"
          @delivery-method-changed="deliveryMethodChanged"
          @select-delivery-courier="selectDeliveryCourier"
        />
      </v-window-item>
      <v-window-item
        v-show="showHallTab"
        value="hall"
        transition="tab-fade-transition"
        reverse-transition="tab-fade-transition"
      >
        <basket-hall
          v-if="deliveryMethod === 'hall'"
          @delivery-method-changed="deliveryMethodChanged"
          @select-delivery-courier="selectDeliveryCourier"
        />
      </v-window-item>
    </v-window>
  </section>
</template>

<style lang="scss" scoped>
.delivery-title {
  margin-bottom: 24px;
  font-size: 18px;
  font-weight: 800;
}

:deep(.v-tab__slider) {
  z-index: -1 !important;
  height: 100% !important;
  padding: 3px 0 !important;
  background: #fff !important;
  border-radius: 100px !important;
}

:deep(.v-btn__overlay),
:deep(.v-btn__underlay) {
  display: none !important;
}

:deep(.v-slide-group__content) {
  width: 100%;
}

.tabs {
  margin-bottom: 24px;
  &--one-tab {
    :deep() {
      height: unset;
      background: unset;
      border-radius: unset;

      .v-tab {
        min-width: unset;
        padding: unset;
        margin: 0;
        font-size: 18px;
        background: unset;
        border-radius: unset;
        box-shadow: unset;
      }

      .v-slide-group__content {
        flex: unset;
      }
    }
  }

  :deep() {
    height: 40px;
    background: $button-background;
    border-radius: 100px;

    .v-tab {
      display: flex;
      flex: 0 1 auto;
      width: 100%;
      max-width: unset;
      height: unset !important;
      margin: 3px;
      font-size: 14px;
      font-weight: 600;
      color: $text-black !important;
      text-transform: none;
      letter-spacing: unset;
      border-radius: 100px !important;
      box-shadow: none !important;

      &:hover {
        background-color: transparent !important;
      }

      &::before {
        display: none;
        opacity: 0 !important;
      }

      &::after {
        display: none;
        opacity: 0 !important;
      }

      @include media('<=tablet') {
        padding: 0 10px;
      }

      &--selected {
        font-weight: 800;
        background: $white;
        box-shadow: 0 4px 15px rgba(90, 72, 160, 0.15);
      }
    }
  }
}
.items {
  min-height: 110px;
}

.tab-fade-transition-enter {
  opacity: 0;
  transition: all 0.15s ease-in-out;
}
.tab-fade-transition-enter-to {
  opacity: 1;
  transition: all 0.15s ease-in-out;
}
.tab-fade-transition-leave {
  position: absolute !important;
  top: 0;
  width: 100%;
  opacity: 1;
  transition: all 0.15s ease-in-out;
}
.tab-fade-transition-leave-to {
  position: absolute !important;
  top: 0;
  width: 100%;
  opacity: 0;
  transition: all 0.15s ease-in-out;
}
</style>
